import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Phone, Send, Brain, RefreshCw } from 'lucide-react';

export interface SMSReplyComposerProps {
  businessId: string;
  value: string;
  onChange: (value: string) => void;
  onSend: (aiGenerated: boolean) => void;  // Updated to include AI flag
  placeholder?: string;
  disabled?: boolean;
  isSending?: boolean;
  error?: string | null;
  className?: string;
  // AI context props
  conversationId?: string;
  customerId?: string;
  phoneNumber?: string;
  prefetchedSuggestion?: string;
  shouldFetchAI?: boolean; // NEW: Control whether to fetch AI suggestions
  onAIStateChange?: (isLoading: boolean, error: string | null, isAiText: boolean) => void; // NEW: Callback for AI state changes
  onRefreshAI?: (refreshHandler: () => void) => void; // NEW: Callback to expose AI refresh handler
}

interface AISuggestion {
  text: string;
  tone: string;
}

export function SMSReplyComposer({
  businessId,
  value,
  onChange,
  onSend,
  placeholder = 'Type your SMS message here...',
  disabled = false,
  isSending = false,
  error = null,
  className = '',
  conversationId,
  customerId,
  phoneNumber,
  prefetchedSuggestion,
  shouldFetchAI = true, // Default to true for backward compatibility
  onAIStateChange, // New prop
  onRefreshAI // New prop
}: SMSReplyComposerProps) {
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [isAiText, setIsAiText] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  
  // Refs for request management
  const currentRequestRef = useRef<AbortController | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRequestKeyRef = useRef<string>('');

  // Create a debounced fetch function
  const debouncedFetchAISuggestion = useCallback(async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create a unique key for this request
    const requestKey = `${businessId}-${conversationId}-${customerId}-${phoneNumber}`;
    
    // Don't make the same request twice
    if (requestKey === lastRequestKeyRef.current && !hasUserInteracted) {
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      if (!businessId || isLoadingAI || hasUserInteracted || value) return;

      // Cancel any existing request
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }

      // Create new abort controller
      currentRequestRef.current = new AbortController();
      lastRequestKeyRef.current = requestKey;
      
      setIsLoadingAI(true);
      setAiError(null);

      try {
        const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            business_id: businessId,
            conversation_id: conversationId,
            customer_id: customerId,
            phone_number: phoneNumber,
            message_type: 'sms',
            recent_messages_count: 10
          }),
          signal: currentRequestRef.current.signal
        });

        if (!response.ok) {
          throw new Error(`Failed to generate suggestions: ${response.status}`);
        }

        const data = await response.json();
        if (data.suggestions && data.suggestions.length > 0) {
          // Use the first suggestion (usually professional tone)
          const suggestion = data.suggestions[0].text;
          setAiSuggestion(suggestion);
          if (!hasUserInteracted && !value) {
            onChange(suggestion);
            setIsAiText(true);
          }
        }
      } catch (err) {
        // Don't show error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('Error fetching AI suggestion:', err);
        setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion');
      } finally {
        setIsLoadingAI(false);
        currentRequestRef.current = null;
      }
    }, 300); // 300ms debounce
  }, [businessId, conversationId, customerId, phoneNumber, hasUserInteracted, value, isLoadingAI]);

  // Use prefetched suggestion when available
  useEffect(() => {
    if (prefetchedSuggestion && !hasUserInteracted && !value) {
      setAiSuggestion(prefetchedSuggestion);
      onChange(prefetchedSuggestion);
      setIsAiText(true);
      return;
    }
  }, [prefetchedSuggestion, hasUserInteracted, value, onChange]);

  // Auto-fetch AI suggestion when component mounts or context changes (only if enabled and no prefetched suggestion)
  useEffect(() => {
    if (shouldFetchAI && businessId && !hasUserInteracted && !value && !prefetchedSuggestion) {
      console.log('🤖 SMSReplyComposer: Auto-fetching AI suggestions');
      debouncedFetchAISuggestion();
    } else if (!shouldFetchAI) {
      console.log('🤖 SMSReplyComposer: AI suggestions disabled');
    }

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }
    };
  }, [shouldFetchAI, businessId, conversationId, customerId, phoneNumber, prefetchedSuggestion]); // Fixed: Don't depend on the function itself

  const handleInputChange = (newValue: string) => {
    setHasUserInteracted(true);
    setIsAiText(false);
    onChange(newValue);
  };

  const handleSend = () => {
    onSend(isAiText);  // Pass the AI flag to the parent
  };

  const handleInputFocus = () => {
    if (isAiText) {
      // Clear the AI text when user focuses to start typing their own message
      setIsAiText(false);
      setHasUserInteracted(true);
      // Keep the text but mark it as user text now
    }
  };

  const handleInputClick = () => {
    if (isAiText) {
      setHasUserInteracted(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isSending && value.trim()) {
        handleSend();
      }
    }
  };

  const handleRefreshAI = async () => {
    // Cancel any existing request
    if (currentRequestRef.current) {
      currentRequestRef.current.abort();
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset states
    setHasUserInteracted(false);
    setIsAiText(false);
    setAiError(null);
    onChange('');
    
    // Create new abort controller
    currentRequestRef.current = new AbortController();
    setIsLoadingAI(true);

    try {
      const response = await fetch(`${process.env.VITE_AI_SERVICE_URL || 'https://booking-app-production-d357.up.railway.app'}/generate-replies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          business_id: businessId,
          conversation_id: conversationId,
          customer_id: customerId,
          phone_number: phoneNumber,
          message_type: 'sms',
          recent_messages_count: 10
        }),
        signal: currentRequestRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Failed to generate suggestions: ${response.status}`);
      }

      const data = await response.json();
      if (data.suggestions && data.suggestions.length > 0) {
        const suggestion = data.suggestions[0].text;
        setAiSuggestion(suggestion);
        onChange(suggestion);
        setIsAiText(true);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error refreshing AI suggestion:', err);
      setAiError(err instanceof Error ? err.message : 'Failed to get AI suggestion');
    } finally {
      setIsLoadingAI(false);
      currentRequestRef.current = null;
    }
  };

  // Call the new prop callback when AI state changes
  useEffect(() => {
    if (onAIStateChange) {
      onAIStateChange(isLoadingAI, aiError, isAiText);
    }
  }, [isLoadingAI, aiError, isAiText, onAIStateChange]);

  // Create a ref to expose the refresh function
  const refreshAIRef = useRef(handleRefreshAI);
  
  // Update ref when handler changes
  useEffect(() => {
    refreshAIRef.current = handleRefreshAI;
  }, [handleRefreshAI]);

  // Call onRefreshAI prop with the current refresh function
  useEffect(() => {
    if (onRefreshAI) {
      onRefreshAI(refreshAIRef.current);
    }
  }, [onRefreshAI]);

  return (
    <div className={className}>
      {/* AI Status Indicator - Moved to toolbar, only show if onAIStateChange not provided (backward compatibility) */}
      {!onAIStateChange && (
        <div className="flex items-center space-x-3 text-[10px] sm:text-xs py-1 min-h-[20px]">
          {(isLoadingAI || aiError || (isAiText && value)) && (
            <>
              <div className="flex items-center space-x-2">
                {isLoadingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-indigo-600 border-t-transparent"></div>
                    <span className="text-indigo-600">Generating AI suggestion...</span>
                  </>
                ) : aiError ? (
                  <span className="text-red-500">AI suggestion failed</span>
                ) : isAiText ? (
                  <>
                    <Brain className="h-3 w-3 text-indigo-600" />
                    <span className="text-indigo-600">AI suggested reply</span>
                  </>
                ) : null}
              </div>
              
              {!isLoadingAI && (
                <button
                  onClick={handleRefreshAI}
                  className="text-indigo-600 hover:text-indigo-800 transition-colors"
                  title="Get new AI suggestion"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* SMS Input - iMessage Style */}
      <div className="relative w-full">
        <textarea
          placeholder={isLoadingAI ? 'Getting AI suggestion...' : placeholder}
          className={`w-full resize-none border border-gray-300 pl-4 pr-12 py-3 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
            isAiText ? 'text-gray-500 italic' : 'text-gray-900'
          }`}
          rows={2}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          disabled={disabled || isSending}
          style={{
            color: isAiText ? '#9CA3AF' : '#111827',
            fontStyle: isAiText ? 'italic' : 'normal'
          }}
        />
        
        {/* iMessage-style Send Button */}
        <button
          disabled={disabled || isSending || !value.trim()}
          onClick={handleSend}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            disabled || isSending || !value.trim()
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600 text-white cursor-pointer shadow-md hover:shadow-lg'
          }`}
          title={isSending ? 'Sending...' : isAiText ? 'Send AI suggestion' : 'Send SMS (Enter)'}
        >
          {isSending ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
          ) : (
            <Send className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="text-xs text-red-500 text-center">
          {error}
        </div>
      )}


    </div>
  );
} 